// NOT the "slimmer FilePreviewPanel that routes kinds itself" the intent rejects: this routes no kinds — it resolves a FileEntry and hands it to PreviewPane, which owns kind routing for both surfaces (FR-026).
import { useCallback, useEffect, useRef } from 'react';
import { TriangleAlertIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { RetryButton } from '@/shared/components/RetryButton';
import { useRailMinifyStore } from '@/shared/store/railMinify';
import { cn } from '@/lib/utils';
import { PreviewPane } from './PreviewPane';
import { useWorkspaceFileEntry } from '../hooks/useWorkspaceFileEntry';

/**
 * The file preview shown beside an open task or an Overview widget (spec 140
 * FR-031, FR-077).
 *
 * Deliberately **not** a modal: not a `Sheet`, not a dialog, no overlay, no
 * focus trap, and no second `onOpenChange`-driven dismissal competing with the
 * host surface's — two of those would make Escape ambiguous (FR-032). It is a
 * plain `<aside>` region in the `GraphDetailPanel` / `BuilderInspector`
 * side-panel idiom, pinned beside whatever is open.
 *
 * It lives in `features/workspace-files` — the module that already owns
 * `PreviewPane`, the classification table and the path resolver — because the
 * Overview hosts it too and it must not depend on `features/tasks` (FR-077).
 * There is one implementation and no second copy on any surface.
 *
 * It also does not know *who* hosts it (F19): the geometry a host imposes is a
 * fact about that host, so each one passes its own `offsetClassName` and the
 * panel keeps no list of surface names and no table of their widths.
 */
export function FilePreviewPanel({
  workspaceId,
  path,
  offsetClassName,
  fallbackFocusRef,
  onClose,
}: {
  workspaceId: string;
  /** The workspace file to preview, from the address's `preview` param. */
  path: string;
  /**
   * How far from the right edge the panel sits, as Tailwind classes the host
   * supplies (FR-074).
   *
   * **Each host writes its literals out in its own source file**, which is the
   * resolution plan.md Open Tradeoff #8 reached, moved rather than abandoned:
   * Tailwind scans source text, so `` `sm:right-[${width}]` `` built at
   * runtime — here or there — is a class that was never generated, and the
   * offset silently becomes zero at `sm` with the panel covering the very
   * drawer the reader clicked in. Literal, in the file that knows the width.
   *
   * Omitted (or empty) means flush to the viewport's right edge.
   */
  offsetClassName?: string;
  /**
   * Where focus goes on close when the control that opened the panel has left
   * the document (FR-037).
   *
   * **Required**, and that is the fix for F22. It used to be optional, falling
   * back to the panel's own `<aside>` — but focus was moved and `onClose()`
   * then unmounted the panel in the same turn, so the element just focused was
   * removed and focus landed on `document.body`: the exact F19 outcome, on the
   * one path that had no other target. The host is the only party that knows
   * an element outliving the panel, so naming one is its job, not a default
   * this component can invent. One host remains (the task sheet, which passes
   * its `SheetContent` ref), so requiring it costs nothing and removes the
   * branch that could not work.
   */
  fallbackFocusRef: React.RefObject<HTMLElement | null>;
  /** Remove `preview` from the address. The only way out of this panel. */
  onClose: () => void;
}) {
  /*
    Resolution (FR-027), owned by `features/workspace-files`' own exported
    resolver rather than re-derived here (FR-070): the parent-folder
    derivation, the listing key and the entry lookup are one module's business,
    and this panel only branches on the answer. The warm path costs no request
    — `useWorkspaceFileExistence` already populates that entry for exactly
    these paths while deciding whether the path in prose was a link at all.
  */
  const { entry, entries, isPending, isError, refetch, isFetching } =
    useWorkspaceFileEntry(workspaceId, path);

  /*
    Focus (FR-037). The control that opened the panel — a path in a comment or
    a results body — is captured at mount, and focus returns to it on close
    where it is still in the document. A swap of files, or a re-render of the
    comment list, can take it away; then focus goes to the element the host
    named in `fallbackFocusRef`, which is required for exactly this reason.

    There is no third fallback, and the panel's own `<aside>` is not one (F22).
    Focus is moved *before* `onClose`, and `onClose` unmounts the panel, so
    focusing anything inside it lands the reader on `document.body` one tick
    later — a fallback that reads as working and is not. The `<aside>` keeps
    `tabIndex={-1}` so the region is reachable while the panel is open, not as
    a close target.

    The fallback is deliberately *not* a `document.querySelector` for the
    sheet's markup (F19): that was a runtime dependency on `ui/Sheet`'s DOM
    from a module that must not know its hosts, and on the surfaces that render
    no sheet — the Workspace Overview — it returned `null` and focus fell to
    `document.body`, the one outcome the comment claimed could not happen.

    Focus is moved *before* `onClose`, because `onClose` is what takes the
    panel (and so the last fallback) out of the document.
  */
  /*
    While the panel is up, the shell's nav rail shows as icons only (FR-078).
    The panel is pinned right and takes the width left of its host, which on a
    narrow viewport reached the rail and covered it outright — and a preview
    that hides the whole app frame reads as a navigation to another page, which
    is the opposite of what this surface is for. Minified rather than hidden:
    the frame stays, so the reader can see they are still where they were.

    Mount/unmount, with the release in the cleanup, so an unmount for any
    reason — close, task switch, route change, an error boundary above — puts
    the rail back. The store is a count, so two panels overlapping during a
    transition cannot restore it early.
  */
  const requestRailMinify = useRailMinifyStore((state) => state.request);
  const releaseRailMinify = useRailMinifyStore((state) => state.release);
  useEffect(() => {
    requestRailMinify();
    return releaseRailMinify;
  }, [requestRailMinify, releaseRailMinify]);

  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const active = document.activeElement;
    /*
      `document.body` is not a trigger. It is what `activeElement` reports when
      nothing is focused, and it passes `document.contains` forever, so keeping
      it would pin focus restoration to `<body>` for good — which is exactly
      what the old FR-037 test had to assert. Treat it as "nothing captured"
      and let the fallback below do its job.
    */
    triggerRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
  }, []);

  const closeAndRestoreFocus = useCallback(() => {
    const trigger = triggerRef.current;
    /*
      Never the panel itself (F22). Focus is moved before `onClose`, because
      `onClose` is what removes the panel — so a target inside the panel is
      gone a tick later and focus falls to `document.body`. Both arms here
      outlive the close: the trigger is checked against the live document, and
      the host's fallback is an element of the host.
    */
    const target =
      trigger && document.contains(trigger)
        ? trigger
        : fallbackFocusRef.current;
    target?.focus();
    onClose();
  }, [fallbackFocusRef, onClose]);

  return (
    <aside
      aria-label="File preview"
      data-slot="file-preview-panel"
      ref={panelRef}
      /*
        Focusable by script, not by Tab (FR-037): `-1` is what lets the panel
        be the last focus target on close when the trigger is gone, without
        inserting the region into the tab order.
      */
      tabIndex={-1}
      /*
        Geometry, in two elements (FR-035, FR-078a as amended 2026-10-14).

        This `<aside>` is a **transparent track**, not the panel's surface: it
        is pinned `inset-y-0 right-0 w-screen`, takes the host's
        `offsetClassName` (the host alone knows how wide the surface the panel
        must sit beside is), reserves a `pl-[4.25rem]` left floor and lays its
        single child out with `flex justify-end`. It paints nothing — no
        border, no background, no shadow — so the strip it reserves on the left
        shows whatever is behind the panel, and because it paints nothing and
        has no overlay, pointer events reach both the card inside it and, in the
        reserved strip, the host's own surface underneath.

        The **card** inside carries the chrome and the `64rem` cap. The floor is
        therefore geometric rather than a `max-width`, which is the amendment:
        the previous `max-w-[min(64rem,calc(100vw-4.25rem))]` could never bind
        on the task-sheet host, whose `offsetClassName` already asks for
        `w-[calc(100vw-44rem)]` — far below `100vw-4.25rem` — so only the
        `64rem` arm could bind, and below a ~1728px viewport the card's left
        edge computed to exactly `0` and covered the rail. With the floor on the
        track, the card's left edge is at least `4.25rem` (the minified rail's
        `3.5rem` — `NavRail`'s `w-14` — plus a `0.75rem` gap) from the track's
        left edge on **every** host and at every width, with no host arithmetic.

        Not draggable, not remembered.
      */
      className={cn(
        /*
          Stacked ABOVE the sheet's overlay, not below it. The task sheet is a
          modal Base UI dialog and `SheetOverlay` is `fixed inset-0 z-50`, so at
          the panel's natural `z-40` the overlay painted across the whole
          viewport on top of the panel: `elementFromPoint` over the panel's own
          close button returned `data-slot="sheet-overlay"`, and a real mouse
          click dismissed the task sheet instead of reaching any panel control
          (found by the quickstart section G walkthrough in a real browser —
          jsdom reports geometry, so the panel tests could not see it). The
          panel is a peer surface the reader works in while the sheet is open
          (FR-031, FR-032: not a modal, no overlay of its own), so it belongs
          above that dim layer rather than behind it.
        */
        'pointer-events-none fixed inset-y-0 z-60 flex pl-[4.25rem]',
        'right-0 w-screen justify-end',
        offsetClassName,
      )}
    >
      {/*
        The card: the panel's actual surface.

        `max-w-[64rem]` is the only width cap here, and the only one anywhere in
        the panel — the track's padding, not a `max-width`, is what keeps the
        left edge off the viewport's. `w-full` so the card fills whatever the
        track leaves after that padding.

        `min-w-[24rem]` is FR-035's floor on the *width*, not a second render
        gate (the only gate is the `sm` breakpoint, decided at the link
        destination, FR-035a). It can out-argue the track's padding: on a
        viewport where `100vw - offset - 4.25rem` is below `24rem`, the card
        stays `24rem` wide and overflows the padding to the left, so the gap
        narrows and can close. That is the same documented trade as before —
        FR-035/FR-035a prefer a clipped, crowded panel to an address carrying a
        `preview` that renders nothing — and it is now the *only* case where the
        floor does not hold, instead of the common one.
      */}
      <div
        data-slot="file-preview-panel-card"
        className="border-border bg-card pointer-events-auto flex w-full min-w-[24rem] max-w-[64rem] flex-col border-l shadow-floating"
      >
        {/*
        There is **one** close control in this panel, and it is the reused
        header's (FR-047, amended). The panel used to draw a second icon button
        of its own in a strip above the header, so a resolved file showed two X
        buttons a few pixels apart — one of which inevitably reads as broken.
        The header's is the one that stays, because it sits in the row that
        carries the file's name and the Download and Copy-path controls, and it
        is given the panel's own name ("Close the file preview") so it is still
        tellable apart from the sheet's "Close task".

        Consequence: the states that render no header carry the close control
        themselves, in the strip below — loading, a failed read, and a
        resolution that found no entry (`PreviewPane`'s `NotFoundState`), which
        is exactly `entry === undefined`. They cannot reuse the header, because
        the header needs an entry none of them has, and without this strip they
        would have no way out of the panel at all. Where there *is* a header,
        this strip is absent, which is what makes the count one rather than two.
      */}
        {entry === undefined ? (
          <div className="flex shrink-0 justify-end px-2 pt-2">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={PANEL_CLOSE_LABEL}
              onClick={closeAndRestoreFocus}
            >
              <XIcon />
            </Button>
          </div>
        ) : null}

        {isPending ? (
          <PendingRegion label="Loading file preview" className="gap-3 p-4">
            {Array.from({ length: 5 }, (_, row) => (
              <Skeleton key={row} className="h-10 w-full" />
            ))}
          </PendingRegion>
        ) : isError ? (
          /*
          A read that failed is not a file that is absent (FR-065). Branched
          **before** the resolved check on purpose: fall through and a backend
          that answered with nothing at all renders as "File not found", which
          tells the reader their file is gone when the request merely failed —
          the one thing they cannot recover from, reported as the one thing
          they must act on. With `retry: false` on the read, one failure is
          final until the reader asks again, which is why the retry is part of
          the state rather than a nicety.
        */
          <section
            role="alert"
            data-testid="file-preview-read-failed"
            className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center"
          >
            <TriangleAlertIcon
              aria-hidden="true"
              className="text-muted-foreground size-6"
              strokeWidth={1.5}
            />
            <h2 className="text-foreground text-xl font-medium">
              Could not read this file
            </h2>
            <p className="text-muted-foreground max-w-prose text-sm">
              The read failed, so there is nothing to show yet. This is not a
              missing file &mdash; try again.
            </p>
            <div className="mt-2">
              <RetryButton
                onRetry={() => void refetch()}
                isFetching={isFetching}
              />
            </div>
          </section>
        ) : entry ? (
          /*
          Hand-off (FR-027a). A matching `file` entry goes straight through as
          `entry`; resolution that finished without one passes the listing it
          did get (possibly empty) together with `selectedFile`, so
          `PreviewPane`'s existing `entries.find` miss renders the existing
          `NotFoundState` — the same branch a renamed or deleted file already
          reaches on the Files page. Either way `PreviewPane` owns kind routing.
        */
          <PreviewPane
            workspaceId={workspaceId}
            entry={entry}
            selectedFile={path}
            views={PREVIEW_ONLY}
            view="preview"
            onViewChange={noop}
            onClose={closeAndRestoreFocus}
            closeLabel={PANEL_CLOSE_LABEL}
            openInFiles
          />
        ) : (
          <PreviewPane
            workspaceId={workspaceId}
            entries={entries ?? []}
            selectedFile={path}
            views={PREVIEW_ONLY}
            view="preview"
            onViewChange={noop}
            onClose={closeAndRestoreFocus}
            closeLabel={PANEL_CLOSE_LABEL}
            openInFiles
          />
        )}
      </div>
    </aside>
  );
}

/**
 * The panel offers the preview only: there is no History tab beside a task, so
 * the header renders no `TabsList` at all, and `view` is fixed — hence the
 * no-op `onViewChange` below, which exists to satisfy the shared header's
 * contract and has nothing to change.
 */
/**
 * The panel's own name for "leave this preview" (FR-047). Given to the reused
 * header rather than drawn a second time beside it, and to the fallback close
 * the header-less states render, so there is one name for one action however
 * the panel resolved.
 */
const PANEL_CLOSE_LABEL = 'Close the file preview';

const PREVIEW_ONLY = ['preview'] as const;
function noop() {}
